/**
 * WHAT "SAVE QUESTION" SAVES — the question the operator ASKED.
 *
 * 🔴 It saved `{ label, metric_id }` and nothing else. The backend then stored
 * `params: {}`, `dimensions: []`, `date_range_spec: null`, and re-ran the
 * metric's DEFAULT. Measured against the real `validateParamDomains`: 16 of the
 * 26 catalogue metrics declare a mandatory param, so their saved question could
 * never run at all; the other 10 re-ran a different question — the operator's
 * channel split, period and grain were discarded. And the metric came from
 * `metric_ids[0]`, the FIRST metric the turn touched, not the table on screen.
 *
 * Pure, so it is tested without React (tests/unit/admin/…).
 */
import type {
  DateRangeSpec,
  QueryInvocation,
  ToolResultRecord,
} from './protocol';

export interface SaveBody {
  metric_id: string;
  params: Record<string, string | number | boolean>;
  dimensions: string[];
  date_range_spec: DateRangeSpec;
}

const DAY_MS = 86_400_000;
const MAX_TRAILING_DAYS = 400;

/** Whole calendar days from `from` through `to`, both included. */
function inclusiveDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

/**
 * The body for `POST /saved-question`, or `null` when the turn produced no
 * answer that can be saved (then the Save button stays disabled).
 *
 * Uses the LAST metric_query that carries an invocation: when a turn runs two
 * queries, the last is the one the model settled on.
 *
 * A window that reaches today is saved as TRAILING days, so the pin keeps
 * meaning "the latest N days" tomorrow. A window wholly in the past is saved
 * as ABSOLUTE, so the pin keeps meaning that period.
 */
export function saveBodyFrom(
  results: readonly ToolResultRecord[],
  todayIso: string,
): SaveBody | null {
  let chosen: { record: ToolResultRecord; invocation: QueryInvocation } | null = null;
  for (const record of results) {
    // `explore` (2026-09-23) saves like a query: its metric id is explore:<relation> and its
    // params carry the canonical spec, so the library replays it by composing.
    if ((record.tool === 'metric_query' || record.tool === 'explore') && record.metric_id && record.invocation) {
      chosen = { record, invocation: record.invocation };
    }
  }
  if (!chosen || !chosen.record.metric_id) return null;
  const { invocation } = chosen;

  const params: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(invocation.params)) {
    if (value !== null) params[key] = value;
  }

  const { from, to } = invocation.window;
  let spec: DateRangeSpec;
  if (to >= todayIso && from <= todayIso) {
    const days = Math.min(MAX_TRAILING_DAYS, Math.max(1, inclusiveDays(from, todayIso)));
    spec = { kind: 'trailing_days', days, grain: invocation.grain };
  } else {
    spec = { kind: 'absolute', from, to, grain: invocation.grain };
  }

  return {
    metric_id: chosen.record.metric_id,
    params,
    dimensions: [...invocation.dimensions],
    date_range_spec: spec,
  };
}
